// --- START OF FILE AdminTasksModal.js ---
// src/components/AdminTasksModal.js

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Modal from 'react-modal';
// Import components from react-select if customizing internal parts
import Select, { components } from 'react-select';
import styles from './AdminTasksModal.module.css'; // Ensure this CSS module exists and is styled
import { useData } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import Spinner from './Spinner'; // Assuming you have a Spinner component
import ConfirmationDialog from './ConfirmationDialog'; // Assuming you have this component
import PasswordStrengthIndicator from './Auth/PasswordStrengthIndicator'; // Import the indicator
import { getPasswordValidationState } from '../utils/validation'; // Import the validation utility

Modal.setAppElement('#root'); // Ensure this matches your app's root element ID

// --- react-select Styles ---
const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: '#3a3a3a',
    borderColor: state.isFocused ? '#6f9aee' : '#555',
    boxShadow: state.isFocused ? '0 0 0 1px #6f9aee' : 'none',
    '&:hover': { borderColor: state.isFocused ? '#6f9aee' : '#777' },
    minHeight: '38px',
    height: 'auto', // Allow height to adjust slightly for single selection
    cursor: 'pointer',
  }),
  valueContainer: (provided) => ({
    // Style the container holding the value/placeholder
    ...provided,
    padding: '2px 8px', // Adjust padding
    maxHeight: '80px', // Limit height to prevent excessive growth in case custom container fails
    overflowY: 'auto', // Allow scrolling if needed (though custom container avoids this)
    scrollbarWidth: 'thin',
    scrollbarColor: '#666 #3a3a3a',
  }),
  input: (provided) => ({
    ...provided,
    margin: '0px',
    padding: '0px', // Ensure input doesn't add extra space
    color: '#e0e0e0',
  }),
  multiValue: (provided) => ({
    // Style individual selected tags (if shown by default container)
    ...provided,
    backgroundColor: '#555',
    borderRadius: '3px',
    margin: '2px 4px 2px 0',
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: '#e0e0e0',
    fontSize: '0.9em',
    padding: '2px 6px',
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: '#ccc',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#c82333', // Red hover for remove
      color: 'white',
    },
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  indicatorsContainer: (provided) => ({
    ...provided,
    alignSelf: 'stretch', // Align indicators vertically
    height: '38px', // Ensure indicators container matches min height
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: '#2d2d2d',
    zIndex: 10, // Ensure dropdown is above other content
    maxHeight: '200px',
    overflowY: 'auto',
    boxShadow: '0 4px 8px rgba(0,0,0,0.5)',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? '#4a4a4a'
      : state.isFocused
        ? '#3a3a3a'
        : '#2d2d2d',
    color: '#e0e0e0',
    cursor: 'pointer',
    '&:active': { backgroundColor: '#4a4a4a' },
    // Style for the Select All option
    fontWeight: state.data.isSelectAll ? 'bold' : 'normal',
    padding: '8px 12px', // Standard option padding
  }),
  singleValue: (provided) => ({ ...provided, color: '#e0e0e0' }),
  placeholder: (provided) => ({ ...provided, color: '#aaa' }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: '#aaa',
    '&:hover': { color: '#ccc' },
  }),
  menuList: (provided) => ({
    ...provided,
    paddingTop: 0, // Remove potential extra padding
    paddingBottom: 0,
    maxHeight: '190px', // Adjust based on menu maxHeight and potential borders/padding
    overflowY: 'auto',
    scrollbarWidth: 'thin',
    scrollbarColor: '#555 #2d2d2d',
  }),
};

// --- Custom Value Container for Multi-Select ---
const CustomValueContainer = ({ children, ...props }) => {
  const { isMulti, selectProps } = props;
  const value = selectProps.value; // This is the array of selected option objects
  const count = Array.isArray(value) ? value.length : 0;

  // Show summary only in multi-select mode when more than 0 items are selected
  // (Show summary even for 1 item in multi-select for consistency)
  if (isMulti && count > 0) {
    const summaryText =
      count === 1 ? `1 influencer selected` : `${count} influencers selected`;
    return (
      <components.ValueContainer {...props}>
        <div
          style={{
            padding: '2px 0',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {summaryText}
        </div>
        {/* Render the Input component after the summary to allow typing */}
        {React.Children.map(children, (child) =>
          child && child.type === components.Input ? child : null,
        )}
      </components.ValueContainer>
    );
  }
  // Otherwise, render default (handles placeholder, single value)
  return (
    <components.ValueContainer {...props}>{children}</components.ValueContainer>
  );
};
// --- End Custom Value Container ---

const AdminTasksModal = () => {
  // --- Context ---
  const {
    isAdminTasksModalOpen,
    setIsAdminTasksModalOpen,
    adminTasksLoading,
    adminTasksError,
    setAdminTasksError,
    allUsers,
    queryResults,
    activeinfluencerId,
    signupUserAdmin,
    moveInfluencers,
    deleteUserAdmin,
    fetchUsers,
  } = useData();
  const { currentUser } = useAuth();

  // --- Local State ---
  // Add User
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserJobDescription, setNewUserJobDescription] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [confirmNewUserPassword, setConfirmNewUserPassword] = useState('');
  const [addUserError, setAddUserError] = useState('');
  const [addUserSuccess, setAddUserSuccess] = useState('');
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [newUserPasswordValidation, setNewUserPasswordValidation] =
    useState(null);
  const [showNewUserPasswordRequirements, setShowNewUserPasswordRequirements] =
    useState(false);
  // Move Influencers
  const [fromUser, setFromUser] = useState(null);
  const [toUser, setToUser] = useState(null);
  const [influencerMoveScope, setInfluencerMoveScope] = useState('current');
  const [influencerMoveTarget, setInfluencerMoveTarget] = useState(null); // Array or single object
  const [moveInfluencersError, setMoveInfluencersError] = useState('');
  const [moveInfluencersSuccess, setMoveInfluencersSuccess] = useState('');
  const [isMovingInfluencers, setIsMovingInfluencers] = useState(false);
  // Delete User
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteUserError, setDeleteUserError] = useState('');
  const [deleteUserSuccess, setDeleteUserSuccess] = useState('');
  const [isDeletingUser, setIsDeletingUser] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteConfirmMessage, setDeleteConfirmMessage] = useState('');

  // Derived State
  const isOverallLoading =
    isAddingUser || isMovingInfluencers || isDeletingUser || adminTasksLoading;

  // --- Memos for Select Options ---
  const userOptions = useMemo(() => {
    // 1. Filter out any null, undefined, or non-object items from the array.
    // 2. Then, map over the valid user objects.
    const options = (allUsers || [])
      .filter((user) => typeof user === 'object' && user !== null)
      .map((user) => ({
        value: user.userId,
        // 3. Add fallbacks for better UX in case of missing data.
        label: `${user.userName || 'Unknown User'} (${user.email || 'No Email'})`,
        userName: user.userName || '',
        email: user.email || '',
        hasInfluencers: user.hasInfluencers || false,
      }));

    // 4. The sorting logic is already perfect and defensive.
    return options.sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }, [allUsers]);

  const influencerScopeOptions = useMemo(
    () => [
      { value: 'current', label: 'Current Influencer' },
      { value: 'queryGroup', label: 'Query Group' },
      { value: 'all', label: 'All Influencers' },
    ],
    [],
  );

  // Base filtered options (influencers owned by fromUser for current/query scopes)
  const baseInfluencerTargetOptions = useMemo(() => {
    if (!fromUser) return [];
    const fromUserId = fromUser.value;
    let options = [];

    if (
      influencerMoveScope === 'current' &&
      activeinfluencerId &&
      Array.isArray(queryResults)
    ) {
      // ASSUMPTION: queryResults items have `userId`
      const activeInf = queryResults.find(
        (inf) => inf.influencerId === activeinfluencerId,
      );
      if (activeInf && activeInf.userId === fromUserId) {
        options.push({
          value: activeInf.influencerId,
          label: `${activeInf.name} (${activeInf.activeInfluencerChannel || 'N/A'})`,
        });
      }
    } else if (
      influencerMoveScope === 'queryGroup' &&
      Array.isArray(queryResults)
    ) {
      options = queryResults
        // 1. First, filter out any null, undefined, or non-object items.
        .filter((inf) => typeof inf === 'object' && inf !== null)
        // 2. Then, filter by userId using optional chaining and a type-safe comparison.
        .filter((inf) => String(inf?.userId) === String(fromUserId))
        // 3. Map to the desired shape, with fallbacks for missing data.
        .map((inf) => ({
          value: inf?.influencerId,
          label: `${inf?.name || 'Unnamed Influencer'} (${inf?.activeInfluencerChannel || 'N/A'})`,
        }))
        // 4. Sort. The existing sort logic is already defensive and perfect.
        .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
    }
    // 'all' scope doesn't generate specific influencer options here
    return options;
  }, [influencerMoveScope, activeinfluencerId, queryResults, fromUser]);

  // Enhanced options including "Select All"
  const enhancedInfluencerTargetOptions = useMemo(() => {
    if (
      influencerMoveScope === 'queryGroup' &&
      baseInfluencerTargetOptions.length > 0
    ) {
      const selectAllOption = {
        value: 'select-all', // Special value
        label: `[x] Select All (${baseInfluencerTargetOptions.length})`,
        isSelectAll: true, // Flag to identify this option
      };
      return [selectAllOption, ...baseInfluencerTargetOptions];
    } else if (influencerMoveScope === 'all') {
      return [{ value: 'all', label: 'All Influencers' }];
    }
    return baseInfluencerTargetOptions;
  }, [influencerMoveScope, baseInfluencerTargetOptions]);

  // Move Button Disabled Logic
  const isMoveButtonDisabled = useMemo(() => {
    if (isOverallLoading) return true;
    if (!fromUser || !toUser || !influencerMoveTarget) return true;
    if (fromUser.value === toUser.value) return true;

    if (influencerMoveScope === 'current') {
      if (
        baseInfluencerTargetOptions.length === 0 ||
        !influencerMoveTarget ||
        influencerMoveTarget.value === 'all'
      )
        return true;
    } else if (influencerMoveScope === 'queryGroup') {
      if (
        baseInfluencerTargetOptions.length === 0 ||
        !Array.isArray(influencerMoveTarget) ||
        influencerMoveTarget.length === 0
      )
        return true;
    } else if (influencerMoveScope === 'all') {
      // ASSUMPTION: allUsers items have `hasInfluencers`
      const sourceUserHasInfluencers = !!allUsers?.find(
        (u) => u.userId === fromUser.value,
      )?.hasInfluencers;
      if (
        !sourceUserHasInfluencers ||
        !influencerMoveTarget ||
        influencerMoveTarget.value !== 'all'
      )
        return true;
    }
    return false;
  }, [
    isOverallLoading,
    fromUser,
    toUser,
    influencerMoveTarget,
    influencerMoveScope,
    baseInfluencerTargetOptions,
    allUsers,
  ]);

  // --- Effects ---
  // Fetch users on modal open
  useEffect(() => {
    if (isAdminTasksModalOpen) fetchUsers();
  }, [isAdminTasksModalOpen, fetchUsers]); // fetchUsers should be memoized by DataContext

  // Clear local state on open/close
  useEffect(() => {
    const resetState = () => {
      setNewUserName('');
      setNewUserEmail('');
      setNewUserJobDescription('');
      setNewUserPassword('');
      setConfirmNewUserPassword('');
      setAddUserError('');
      setAddUserSuccess('');
      setIsAddingUser(false);
      setNewUserPasswordValidation(null);
      setShowNewUserPasswordRequirements(false);
      setFromUser(null);
      setToUser(null);
      setInfluencerMoveScope('current');
      setInfluencerMoveTarget(null); // Reset target
      setMoveInfluencersError('');
      setMoveInfluencersSuccess('');
      setIsMovingInfluencers(false);
      setUserToDelete(null);
      setDeleteUserError('');
      setDeleteUserSuccess('');
      setIsDeletingUser(false);
      setShowDeleteConfirmation(false);
      setDeleteConfirmMessage('');
      setAdminTasksError(null); // Clear context error too
    };

    if (isAdminTasksModalOpen) {
      resetState();
    }
    // Optional: Reset on close too, though unmounting might handle it
    // return () => { if (!isAdminTasksModalOpen) resetState(); };
  }, [isAdminTasksModalOpen, setAdminTasksError]);

  // Update influencer move target selection based on scope/options/fromUser
  useEffect(() => {
    if (!isAdminTasksModalOpen) return; // Don't run if modal is closed

    if (
      influencerMoveScope === 'current' &&
      baseInfluencerTargetOptions.length > 0
    ) {
      setInfluencerMoveTarget(baseInfluencerTargetOptions[0]); // Auto-select current
    } else if (influencerMoveScope === 'all') {
      setInfluencerMoveTarget({ value: 'all', label: 'All Influencers' }); // Auto-select the 'all' object
    } else {
      // Clear for queryGroup or if current has no valid option
      setInfluencerMoveTarget(influencerMoveScope === 'queryGroup' ? [] : null);
    }
    // Clear errors/success when dependencies change
    setMoveInfluencersError('');
    setMoveInfluencersSuccess('');
  }, [
    influencerMoveScope,
    baseInfluencerTargetOptions,
    fromUser,
    isAdminTasksModalOpen,
  ]); // Added isAdminTasksModalOpen

  // Validate new user password
  useEffect(() => {
    if (!isAdminTasksModalOpen) return;
    if (newUserPassword) {
      const validation = getPasswordValidationState(newUserPassword);
      setNewUserPasswordValidation(validation);
      setShowNewUserPasswordRequirements(true);
    } else {
      setNewUserPasswordValidation(null);
      setShowNewUserPasswordRequirements(false);
    }
  }, [newUserPassword, isAdminTasksModalOpen]);

  // --- Handlers ---
  const handleCloseRequest = useCallback(() => {
    if (isOverallLoading || showDeleteConfirmation) return; // Prevent close during action or confirmation
    setIsAdminTasksModalOpen(false);
  }, [isOverallLoading, showDeleteConfirmation, setIsAdminTasksModalOpen]);

  // Password Focus/Blur
  const handleNewUserPasswordFocus = () => {
    setShowNewUserPasswordRequirements(true);
  };
  const handleNewUserPasswordBlur = () => {
    if (!newUserPassword) {
      setShowNewUserPasswordRequirements(false);
    }
  };

  // Add User
  const handleAddUserSubmit = async (e) => {
    e.preventDefault();
    setAddUserError('');
    setAddUserSuccess('');
    setAdminTasksError(null);

    if (
      !newUserName.trim() ||
      !newUserEmail.trim() ||
      !newUserPassword ||
      !confirmNewUserPassword
    ) {
      setAddUserError('All fields are required.');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(newUserEmail.trim())) {
      setAddUserError('Invalid email format.');
      return;
    }
    if (newUserPassword !== confirmNewUserPassword) {
      setAddUserError('Passwords do not match.');
      return;
    }
    const validation = getPasswordValidationState(newUserPassword);
    setNewUserPasswordValidation(validation);
    setShowNewUserPasswordRequirements(true);
    if (!validation.isValid) {
      setAddUserError(
        'Password does not meet all requirements. Please check the list below.',
      );
      return;
    }

    setIsAddingUser(true);
    try {
      const userData = {
        userName: newUserName.trim(),
        email: newUserEmail.trim(),
        password: newUserPassword,
        jobDescription: newUserJobDescription,
      };
      const result = await signupUserAdmin(userData);
      if (result.status) {
        setAddUserSuccess(`User "${newUserName.trim()}" added successfully.`);
        setTimeout(() => setAddUserSuccess(''), 3000);
        setNewUserName('');
        setNewUserEmail('');
        setNewUserJobDescription('');
        setNewUserPassword('');
        setConfirmNewUserPassword('');
        setNewUserPasswordValidation(null);
        setShowNewUserPasswordRequirements(false);
        await fetchUsers();
      } else {
        setAddUserError(result.message || 'Failed to add user.');
      }
    } catch (error) {
      console.error('[AdminTasksModal] Add user unexpected error:', error);
      setAddUserError('An unexpected error occurred during signup.');
    } finally {
      setIsAddingUser(false);
    }
  };

  // Influencer Target Select Change (Handles Select All)
  const handleInfluencerTargetChange = (selectedOptionsOrOption) => {
    if (influencerMoveScope !== 'queryGroup') {
      setInfluencerMoveTarget(selectedOptionsOrOption);
      return;
    }
    const selectedOptions = selectedOptionsOrOption || [];
    const selectAllWasClicked = selectedOptions.some((opt) => opt.isSelectAll);

    if (selectAllWasClicked) {
      const allActualInfluencersSelected =
        influencerMoveTarget.length === baseInfluencerTargetOptions.length;
      if (allActualInfluencersSelected) {
        setInfluencerMoveTarget([]);
      } // Deselect all
      else {
        setInfluencerMoveTarget(baseInfluencerTargetOptions);
      } // Select all actual
    } else {
      setInfluencerMoveTarget(selectedOptions); // Update with selection
    }
  };

  // Move Influencers
  const handleMoveInfluencersSubmit = async (e) => {
    e.preventDefault();
    setMoveInfluencersError('');
    setMoveInfluencersSuccess('');
    setAdminTasksError(null);

    // Validation
    if (
      !fromUser ||
      !toUser ||
      !influencerMoveTarget ||
      fromUser.value === toUser.value
    ) {
      setMoveInfluencersError(
        'Please select valid source/destination users and influencers to move.',
      );
      return;
    }
    let hasInfluencersToMove = false;
    if (influencerMoveScope === 'current') {
      hasInfluencersToMove =
        baseInfluencerTargetOptions.length > 0 &&
        influencerMoveTarget &&
        influencerMoveTarget.value !== 'all';
    } else if (influencerMoveScope === 'queryGroup') {
      hasInfluencersToMove =
        baseInfluencerTargetOptions.length > 0 &&
        Array.isArray(influencerMoveTarget) &&
        influencerMoveTarget.length > 0;
    } else if (influencerMoveScope === 'all') {
      // ASSUMPTION: allUsers items have `hasInfluencers`
      hasInfluencersToMove = !!allUsers?.find(
        (u) => u.userId === fromUser.value,
      )?.hasInfluencers;
      if (!influencerMoveTarget || influencerMoveTarget.value !== 'all')
        hasInfluencersToMove = false;
    }
    if (!hasInfluencersToMove) {
      setMoveInfluencersError(
        'No eligible influencers found for the selected user and scope.',
      );
      return;
    }

    setIsMovingInfluencers(true);
    // 1. Provide a safe default value for the payload.
    let influencerIdsToMovePayload = [];
    if (influencerMoveScope === 'all') {
      influencerIdsToMovePayload = 'all';
    } else if (influencerMoveScope === 'current') {
      // 2. Use optional chaining to safely access the 'value' property.
      // This prevents a crash if influencerMoveTarget is null or undefined.
      if (influencerMoveTarget?.value !== undefined) {
        influencerIdsToMovePayload = [influencerMoveTarget.value];
      }
    } else if (influencerMoveScope === 'queryGroup') {
      // 3. First, ensure the target is actually an array.
      if (Array.isArray(influencerMoveTarget)) {
        influencerIdsToMovePayload = influencerMoveTarget
          // 4. Filter out any items that are null or don't have a 'value'.
          .filter(
            (opt) => opt && typeof opt === 'object' && opt.value !== undefined,
          )
          // 5. Safely map the remaining items to their values.
          .map((opt) => opt.value);
      }
    }
    try {
      const result = await moveInfluencers(
        fromUser.value,
        toUser.value,
        influencerIdsToMovePayload,
      );
      if (result.status) {
        setIsMovingInfluencers(false);
        setTimeout(() => setMoveInfluencersSuccess(''), 3000);
        setMoveInfluencersSuccess(
          result.message || 'Influencers moved successfully.',
        );
        await fetchUsers(); // Refresh user list
        // Reset selections after successful move? Optional.
        setFromUser(null);
        setToUser(null); // Uncomment to clear
      } else {
        setMoveInfluencersError(
          result.message || 'Failed to move influencers.',
        );
      }
    } catch (error) {
      console.error(
        '[AdminTasksModal] Move influencers unexpected error:',
        error,
      );
      setMoveInfluencersError(
        'An unexpected error occurred during the move operation.',
      );
    } finally {
      setIsMovingInfluencers(false);
    }
  };

  // Delete User
  const handleDeleteUserClick = () => {
    setDeleteUserError('');
    setDeleteUserSuccess('');
    setAdminTasksError(null);
    if (!userToDelete) {
      setDeleteUserError('Please select a user to delete.');
      return;
    }
    if (userToDelete.value === currentUser?.uid) {
      setDeleteUserError('You cannot delete your own user account.');
      return;
    }
    // ASSUMPTION: allUsers items have `hasInfluencers`
    const userToDeleteData = allUsers?.find(
      (u) => u.userId === userToDelete.value,
    );
    if (userToDeleteData?.hasInfluencers) {
      setDeleteUserError(
        'User still has associated influencers. Please reassign them before deleting.',
      );
      return;
    }
    setDeleteConfirmMessage(
      `WARNING: This action will permanently delete user "${userToDelete.label}" and ALL their associated data (influencers, tasks, messages, etc.). This action is irreversible.`,
    );
    setShowDeleteConfirmation(true);
  };

  const confirmDeleteUser = async () => {
    setShowDeleteConfirmation(false);
    setIsDeletingUser(true);
    setDeleteUserError('');
    setDeleteUserSuccess('');
    setAdminTasksError(null);
    if (!userToDelete || userToDelete.value === currentUser?.uid) {
      setDeleteUserError(
        'Invalid user selected for deletion or cannot delete self.',
      );
      setIsDeletingUser(false);
      return;
    }
    // ASSUMPTION: allUsers items have `hasInfluencers`
    const userToDeleteData = allUsers?.find(
      (u) => u.userId === userToDelete.value,
    );
    if (userToDeleteData?.hasInfluencers) {
      setDeleteUserError(
        'User still has associated influencers. Deletion aborted.',
      );
      setIsDeletingUser(false);
      return;
    }

    try {
      const result = await deleteUserAdmin(userToDelete.value);
      if (result.status) {
        setDeleteUserSuccess(`User deleted successfully.`);
        setTimeout(() => setDeleteUserSuccess(''), 3000);
        setUserToDelete(null); // Clear selection
        await fetchUsers(); // Refresh user list
      } else {
        setDeleteUserError(result.message || 'Failed to delete user.');
      }
    } catch (error) {
      console.error('[AdminTasksModal] Delete user unexpected error:', error);
      setDeleteUserError('An unexpected error occurred during deletion.');
    } finally {
      setIsDeletingUser(false);
    }
  };

  const cancelDeleteUser = () => {
    setShowDeleteConfirmation(false);
  };

  // Filter out current user for 'From' and 'Delete' dropdowns
  const filteredUserOptionsForDeleteAndFrom = userOptions.filter(
    (user) => user.value !== currentUser?.uid,
  );

  // --- Render ---
  if (!isAdminTasksModalOpen) {
    return null;
  }

  return (
    <>
      <Modal
        isOpen={isAdminTasksModalOpen}
        onRequestClose={handleCloseRequest}
        className={`${styles.modalContent} ${isOverallLoading ? styles.loading : ''}`}
        overlayClassName={styles.modalOverlay}
        contentLabel="Admin Tasks"
        shouldCloseOnOverlayClick={!isOverallLoading && !showDeleteConfirmation}
        shouldCloseOnEsc={!isOverallLoading && !showDeleteConfirmation}
        aria={{ modal: 'true', labelledby: 'adminTasksModalTitle' }}
      >
        {/* Spinner Overlay */}
        {isOverallLoading && (
          <Spinner
            message={
              isAddingUser
                ? `Adding New User ${newUserName}...`
                : isMovingInfluencers
                  ? `Moving Influencers from user ${fromUser.label} to user ${toUser.label}...`
                  : isDeletingUser
                    ? `Deleting User ...`
                    : adminTasksLoading
                      ? 'Loading Users...'
                      : 'Processing...'
            }
          />
        )}
        <h2 id="adminTasksModalTitle">Admin Tasks</h2>
        {/* General Modal Error (from DataContext) */}
        {adminTasksError && (
          <div className={styles.taskError}>
            Context Error: {adminTasksError}
          </div>
        )}
        <div className={styles.modalBody}>
          {/* Left Panel: Add User */}
          <div className={styles.leftPanel}>
            <h3>Add New User</h3>
            <form onSubmit={handleAddUserSubmit} noValidate>
              <div className={styles.formGroup}>
                <label htmlFor="newUserNameInput">Username:</label>
                <input
                  type="text"
                  id="newUserNameInput"
                  value={newUserName}
                  onChange={(e) => setNewUserName(e.target.value)}
                  placeholder="Enter Username"
                  disabled={isOverallLoading}
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="newUserEmailInput">Email:</label>
                <input
                  type="email"
                  id="newUserEmailInput"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  placeholder="Enter Email"
                  disabled={isOverallLoading}
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="newUserJobInput">Job Description:</label>
                <textarea
                  type="text"
                  id="newUserJobInput"
                  value={newUserJobDescription}
                  onChange={(e) => setNewUserJobDescription(e.target.value)}
                  rows="3"
                  placeholder="Enter Job Description"
                  disabled={isOverallLoading}
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="newUserPasswordInput">Initial Password:</label>
                <input
                  type="password"
                  id="newUserPasswordInput"
                  value={newUserPassword}
                  onChange={(e) => setNewUserPassword(e.target.value)}
                  onFocus={handleNewUserPasswordFocus}
                  onBlur={handleNewUserPasswordBlur}
                  placeholder="Create a strong password"
                  disabled={isOverallLoading}
                  required
                  autoComplete="new-password"
                  aria-describedby={
                    showNewUserPasswordRequirements
                      ? 'new-user-password-reqs'
                      : undefined
                  }
                />
                {showNewUserPasswordRequirements && (
                  <div id="new-user-password-reqs">
                    <PasswordStrengthIndicator
                      validationState={newUserPasswordValidation}
                    />
                  </div>
                )}
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="confirmNewUserPasswordInput">
                  Confirm Password:
                </label>
                <input
                  type="password"
                  id="confirmNewUserPasswordInput"
                  value={confirmNewUserPassword}
                  onChange={(e) => setConfirmNewUserPassword(e.target.value)}
                  placeholder="Confirm Password"
                  disabled={isOverallLoading}
                  required
                  autoComplete="new-password"
                />
              </div>
              <button
                type="submit"
                className={styles.actionButton}
                disabled={isOverallLoading}
              >
                {isAddingUser ? 'Adding...' : 'Add User'}
              </button>
            </form>
            {/* Local Add User Errors/Success */}
            {addUserSuccess && (
              <div className={styles.taskSuccess}>{addUserSuccess}</div>
            )}
            {addUserError && !adminTasksError && (
              <div className={styles.taskError}>{addUserError}</div>
            )}
          </div>{' '}
          {/* End Left Panel */}
          {/* Right Panel */}
          <div className={styles.rightPanel}>
            {/* Section: Move Influencers */}
            <h3>Move Influencers</h3>
            <div className={styles.formGroup}>
              <label htmlFor="fromUserInput">From User:</label>
              <Select
                inputId="fromUserInput"
                options={filteredUserOptionsForDeleteAndFrom}
                value={fromUser}
                onChange={setFromUser}
                styles={customSelectStyles}
                isDisabled={isOverallLoading}
                placeholder="Select Source User..."
                isClearable={true}
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="toUserInput">To User:</label>
              <Select
                inputId="toUserInput"
                options={userOptions}
                value={toUser}
                onChange={setToUser}
                styles={customSelectStyles}
                isDisabled={isOverallLoading}
                placeholder="Select Destination User..."
                isClearable={true}
              />
            </div>
            <div className={styles.formGroup}>
              <label>Influencers to Move:</label>
              <div
                className={styles.radioGroup}
                style={{ marginBottom: '10px' }}
              >
                {(influencerScopeOptions || []).map((option) => (
                  <label key={option.value}>
                    <input
                      type="radio"
                      name="influencerScope"
                      value={option.value}
                      checked={influencerMoveScope === option.value}
                      onChange={(e) => setInfluencerMoveScope(e.target.value)}
                      disabled={isOverallLoading || !fromUser}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
              {/* Influencer Target Select */}
              {influencerMoveScope !== 'all' && (
                <Select
                  inputId="influencerTargetSelect"
                  options={enhancedInfluencerTargetOptions}
                  value={influencerMoveTarget}
                  onChange={handleInfluencerTargetChange}
                  styles={customSelectStyles}
                  components={{ ValueContainer: CustomValueContainer }}
                  isDisabled={
                    isOverallLoading ||
                    !fromUser ||
                    (influencerMoveScope !== 'queryGroup' &&
                      baseInfluencerTargetOptions.length === 0)
                  } // Disable if no source or no base options for current
                  placeholder={
                    !fromUser
                      ? "Select 'From User' first"
                      : baseInfluencerTargetOptions.length === 0
                        ? influencerMoveScope === 'current'
                          ? 'Current influencer not owned by source'
                          : 'Source owns no influencers in query'
                        : // If options exist
                          influencerMoveScope === 'current'
                          ? 'Current Influencer Selected'
                          : 'Select Influencers...'
                  }
                  isMulti={influencerMoveScope === 'queryGroup'}
                  isClearable={influencerMoveScope !== 'current'}
                  noOptionsMessage={() =>
                    !fromUser
                      ? "Select 'From User' first"
                      : 'No matching influencers found'
                  }
                  closeMenuOnSelect={influencerMoveScope !== 'queryGroup'}
                  hideSelectedOptions={false}
                  controlShouldRenderValue={
                    influencerMoveScope !== 'queryGroup'
                  }
                />
              )}
              {/* Display text for 'All Influencers' mode */}
              {influencerMoveScope === 'all' && (
                <div className={styles.influencerTargetText}>
                  {!fromUser
                    ? "Select 'From User' first"
                    : !allUsers?.find((u) => u.userId === fromUser.value)
                          ?.hasInfluencers
                      ? 'Source user has no influencers'
                      : 'All Influencers owned by source user'}
                </div>
              )}
              {/* Specific Hints */}
              {fromUser &&
                influencerMoveScope === 'current' &&
                baseInfluencerTargetOptions.length === 0 &&
                activeinfluencerId && (
                  <p className={styles.hintText}>
                    The currently active influencer is not owned by the selected
                    'From User'.
                  </p>
                )}
              {fromUser &&
                influencerMoveScope === 'queryGroup' &&
                baseInfluencerTargetOptions.length === 0 &&
                queryResults &&
                queryResults.length > 0 && (
                  <p className={styles.hintText}>
                    None of the influencers in the current query results are
                    owned by the selected 'From User'.
                  </p>
                )}
            </div>
            <button
              type="button"
              onClick={handleMoveInfluencersSubmit}
              className={`${styles.actionButton} ${styles.moveButton}`}
              disabled={isMoveButtonDisabled}
              title={
                isMoveButtonDisabled
                  ? 'Select valid source/destination users and ensure source user owns eligible influencers for the selected scope.'
                  : 'Move selected influencers'
              }
            >
              {isMovingInfluencers ? 'Moving...' : 'Move Influencers'}
            </button>
            {/* Local Move Errors/Success */}
            {moveInfluencersSuccess && (
              <div className={styles.taskSuccess}>{moveInfluencersSuccess}</div>
            )}
            {moveInfluencersError && !adminTasksError && (
              <div className={styles.taskError}>{moveInfluencersError}</div>
            )}

            {/* Section: Delete User */}
            <h3 style={{ marginTop: '40px' }}>Delete User</h3>
            {/* Local Delete Errors/Success */}
            {deleteUserSuccess && (
              <div className={styles.taskSuccess}>{deleteUserSuccess}</div>
            )}
            {deleteUserError && !adminTasksError && (
              <div className={styles.taskError}>{deleteUserError}</div>
            )}

            <div className={styles.formGroup}>
              <label htmlFor="userToDeleteInput">User to Delete:</label>
              <Select
                inputId="userToDeleteInput"
                options={filteredUserOptionsForDeleteAndFrom}
                value={userToDelete}
                onChange={setUserToDelete}
                styles={customSelectStyles}
                isDisabled={isOverallLoading}
                placeholder="Select User to Delete..."
                isClearable={true}
              />
              {userToDelete &&
                allUsers?.find((u) => u.userId === userToDelete.value)
                  ?.hasInfluencers && (
                  <p className={styles.hintTextWarning}>
                    This user has associated influencers. You must move them
                    before deleting the user.
                  </p>
                )}
            </div>
            <button
              type="button"
              onClick={handleDeleteUserClick}
              className={`${styles.actionButton} ${styles.deleteButton}`}
              disabled={
                isOverallLoading ||
                !userToDelete ||
                !!allUsers?.find((u) => u.userId === userToDelete.value)
                  ?.hasInfluencers
              } // Ensure boolean check for hasInfluencers
            >
              {isDeletingUser ? 'Deleting...' : 'Delete User'}
            </button>
          </div>{' '}
          {/* End Right Panel */}
        </div>{' '}
        {/* End Modal Body */}
        {/* Footer - Close Button */}
        <div className={styles.modalActions}>
          <button
            onClick={handleCloseRequest}
            className={`${styles.actionButton} ${styles.closeButton}`}
            disabled={isOverallLoading || showDeleteConfirmation} // Also disable close during confirmation
          >
            Close
          </button>
        </div>
      </Modal>

      {/* Confirmation Dialog */}
      {showDeleteConfirmation && (
        <ConfirmationDialog
          isOpen={showDeleteConfirmation}
          message={deleteConfirmMessage}
          onConfirm={confirmDeleteUser}
          onCancel={cancelDeleteUser}
          isDanger={true}
        />
      )}
    </>
  );
};

export default AdminTasksModal;
// --- END OF FILE AdminTasksModal.js ---
